import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Badge,
  Table,
  Modal,
  Form,
  CardHeader,
} from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import orderService from "../../services/orderService";
import deliveryProfileService from "../../services/deliveryProfileService";
import ratingService from "../../services/ratingService";
import paymentService from "../../services/paymentService";
import api from "../../services/api";
import ConfirmModal from "../Common/ConfirmModal";
import { formatDateTimeToDDMMYYYY12Hr } from "../../utils/dateUtils";
import { formatQuantity, formatQuantityWithUnit, formatDetailedQuantity } from "../../utils/quantityUtils";
import { FaFileInvoice, FaPrint } from "react-icons/fa";

const OrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useSelector((state) => state.auth);
  const isVendor = user?.user_type === "vendor";
  const [order, setOrder] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [statusUpdateType, setStatusUpdateType] = useState("");

  // New state for delivery partner selection modal
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [selectedDeliveryPartner, setSelectedDeliveryPartner] = useState("");
  const [deliveryPartnersLoading, setDeliveryPartnersLoading] = useState(false);

  // New state for cancellation reason modal
  const [showCancelReasonModal, setShowCancelReasonModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");

  // States for 3PL Shipping & Logistics
  const [booking3PL, setBooking3PL] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualCourier, setManualCourier] = useState("Delhivery");
  const [manualAWB, setManualAWB] = useState("");
  const [manualTrackingUrl, setManualTrackingUrl] = useState("https://track.delhivery.com/shipping");
  const [manualShipmentId, setManualShipmentId] = useState("");
  const [savingManual, setSavingManual] = useState(false);
  const [shadowfaxTracking, setShadowfaxTracking] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [loadingPackingSlip, setLoadingPackingSlip] = useState(false);

  // State for ratings
  const [productRatings, setProductRatings] = useState({});
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingToDelete, setRatingToDelete] = useState(null);
  const [showDeleteRatingModal, setShowDeleteRatingModal] = useState(false);
  const [deletingRating, setDeletingRating] = useState(false);

  // Check if user can delete ratings (admin, super_admin, vendor)
  const canDeleteRatings = user && ["admin", "super_admin", "vendor"].includes(user.user_type);

  const [activeReturnRequest, setActiveReturnRequest] = useState(null);
  const [loadingReturnRequest, setLoadingReturnRequest] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnType, setReturnType] = useState("return");
  const [returnReason, setReturnReason] = useState("");
  const [returnNotes, setReturnNotes] = useState("");
  const [returnUpiId, setReturnUpiId] = useState("");
  const [selectedReturnItems, setSelectedReturnItems] = useState({});
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [returnImages, setReturnImages] = useState([]);
  const [returnVideos, setReturnVideos] = useState([]);
  const [returnImagePreviews, setReturnImagePreviews] = useState([]);

  useEffect(() => {
    if (id) {
      fetchOrderDetails();
      fetchOrderTimeline();
      fetchActiveReturnRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch ratings and tracking details when order is loaded
  useEffect(() => {
    if (order && order.items && order.items.length > 0) {
      fetchProductRatings();
    }
    if (order && order.awb_number) {
      fetchShadowfaxTracking(order.awb_number);
    } else {
      setShadowfaxTracking(null);
      setTrackingError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const fetchShadowfaxTracking = async (awb) => {
    if (!awb) return;
    setLoadingTracking(true);
    setTrackingError(null);
    try {
      // Split by comma in case of multi-vendor comma-separated AWBs
      const cleanAwb = String(awb).split(",")[0].trim();
      const isReverse = cleanAwb.startsWith("R");
      const courierPartner = String(order?.courier_partner || "").toLowerCase();
      let response;

      if (courierPartner === "delhivery") {
        if (isReverse) {
          response = await orderService.getDelhiveryReverseTracking(cleanAwb);
        } else {
          response = await orderService.getDelhiveryTracking(cleanAwb);
        }
      } else if (courierPartner === "xpressbees") {
        if (isReverse) {
          response = await orderService.getXpressbeesReverseTracking(cleanAwb);
        } else {
          response = await orderService.getXpressbeesTracking(cleanAwb);
        }
      } else {
        if (isReverse) {
          response = await orderService.getShadowfaxReverseTracking(cleanAwb);
        } else {
          response = await orderService.getShadowfaxTracking(cleanAwb);
        }
      }

      if (response && response.success && response.data) {
        setShadowfaxTracking(response.data);
      } else {
        setShadowfaxTracking(null);
        setTrackingError(response?.message || "No tracking details available for this shipment.");
      }
    } catch (err) {
      console.error("Error fetching courier tracking:", err);
      setShadowfaxTracking(null);
      setTrackingError(err.response?.data?.message || err.response?.data?.error || "Tracking details are not available at this moment.");
    } finally {
      setLoadingTracking(false);
    }
  };

  const fetchActiveReturnRequest = async () => {
    try {
      setLoadingReturnRequest(true);
      const response = await api.get(`/api/returns?order_id=${id}`);
      if (response.data && response.data.success && response.data.data.length > 0) {
        const active = response.data.data.find(r => r.status !== "rejected" && r.status !== "cancelled");
        setActiveReturnRequest(active || null);
      } else {
        setActiveReturnRequest(null);
      }
    } catch (err) {
      console.error("Error fetching active return request:", err);
    } finally {
      setLoadingReturnRequest(false);
    }
  };

  const handleOpenReturnModal = () => {
    if (!order || !order.items) return;
    const initialSelected = {};
    order.items.forEach(item => {
      initialSelected[item.product_id] = {
        selected: true,
        quantity: item.quantity || 1
      };
    });
    setSelectedReturnItems(initialSelected);
    setReturnType("return");
    setReturnReason("");
    setReturnNotes("");
    setReturnUpiId("");
    setReturnImages([]);
    setReturnVideos([]);
    setReturnImagePreviews([]);
    setShowReturnModal(true);
  };

  const handleReturnImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const combined = [...returnImages, ...files].slice(0, 5);
    setReturnImages(combined);
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setReturnImagePreviews((prev) => [...prev, ...newPreviews].slice(0, 5));
  };

  const handleRemoveReturnImage = (index) => {
    setReturnImages((prev) => prev.filter((_, i) => i !== index));
    setReturnImagePreviews((prev) => {
      try {
        URL.revokeObjectURL(prev[index]);
      } catch (err) { }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleReturnVideosChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setReturnVideos((prev) => [...prev, ...files].slice(0, 2));
  };

  const handleRemoveReturnVideo = (index) => {
    setReturnVideos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReturnItemToggle = (productId) => {
    setSelectedReturnItems(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        selected: !prev[productId].selected
      }
    }));
  };

  const handleReturnQtyChange = (productId, newQty) => {
    setSelectedReturnItems(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        quantity: parseInt(newQty) || 1
      }
    }));
  };

  const handleSubmitReturnRequest = async (e) => {
    e.preventDefault();
    if (!returnReason.trim()) {
      toast.warning("Please provide a reason for the return/exchange.");
      return;
    }

    const payloadItems = Object.keys(selectedReturnItems)
      .filter(pId => selectedReturnItems[pId].selected)
      .map(pId => ({
        product_id: pId,
        quantity: selectedReturnItems[pId].quantity
      }));

    if (payloadItems.length === 0) {
      toast.warning("Please select at least one item to return/exchange.");
      return;
    }

    try {
      setSubmittingReturn(true);
      const formData = new FormData();
      formData.append("order_id", order.id);
      formData.append("type", returnType);
      formData.append("reason", returnReason);
      formData.append("notes", returnNotes || "");
      if (returnType === "return" && returnUpiId.trim()) {
        const upiRegex = /^[\w.-]+@[\w.-]+$/;
        if (!upiRegex.test(returnUpiId.trim())) {
          toast.warning("Please enter a valid UPI ID (e.g. 9876543210@paytm or username@bank)");
          setSubmittingReturn(false);
          return;
        }
        formData.append("upi_id", returnUpiId.trim());
      }
      formData.append("items", JSON.stringify(payloadItems));

      // Append proof images (each file only once)
      returnImages.forEach((imgFile) => {
        formData.append("images", imgFile);
      });

      // Append proof videos (each file only once)
      returnVideos.forEach((vidFile) => {
        formData.append("videos", vidFile);
      });

      const response = await api.post("/api/returns", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data && response.data.success) {
        toast.success(`${returnType === "return" ? "Return" : "Exchange"} request submitted successfully!`);
        setShowReturnModal(false);
        fetchActiveReturnRequest();
        fetchOrderDetails();
      } else {
        toast.error("Failed to submit return request");
      }
    } catch (err) {
      console.error("Error submitting return request:", err);
      toast.error(err.response?.data?.message || "Failed to submit request");
    } finally {
      setSubmittingReturn(false);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderService.getOrderById(id);
      // orderService.getOrderById already returns response.data, so response is the data object
      const orderData = response.order || response.data?.order || response;
      setOrder(orderData);
      fetchActiveReturnRequest();
    } catch (err) {
      setError("Failed to fetch order details");
      console.error("Error fetching order details:", err);
      toast.error("Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderTimeline = async () => {
    try {
      const response = await orderService.getOrderTimeline(id);
      // orderService.getOrderTimeline already returns response.data, so response is the data object
      setTimeline(response.timeline || response.data?.timeline || []);
    } catch (err) {
      console.error("Error fetching order timeline:", err);
      // Don't show error toast for timeline as it's not critical
    }
  };

  // Fetch ratings for all products in the order
  const fetchProductRatings = async () => {
    if (!order || !order.items || order.items.length === 0) return;

    try {
      setRatingsLoading(true);
      const productIds = order.items.map(item => item.product_id).filter(Boolean);

      if (productIds.length === 0) {
        setRatingsLoading(false);
        return;
      }

      // Fetch ratings for each product, filtering by order_id if available
      const ratingsMap = {};

      await Promise.all(
        productIds.map(async (productId) => {
          try {
            const response = await ratingService.getProductRatings(productId);
            // Response format: { ratings: [...], summary: {...} }
            const ratings = response?.ratings || [];

            // Filter ratings by order_id to show only ratings for this specific order
            const orderRatings = order.id
              ? ratings.filter(rating => rating.order_id === order.id)
              : ratings;

            ratingsMap[productId] = orderRatings;
          } catch (error) {
            console.error(`Error fetching ratings for product ${productId}:`, error);
            ratingsMap[productId] = [];
          }
        })
      );

      setProductRatings(ratingsMap);
    } catch (error) {
      console.error("Error fetching product ratings:", error);
    } finally {
      setRatingsLoading(false);
    }
  };

  // Handle delete rating
  const handleDeleteRating = (ratingId, productId) => {
    setRatingToDelete({ id: ratingId, productId });
    setShowDeleteRatingModal(true);
  };

  // Confirm delete rating
  const confirmDeleteRating = async () => {
    if (!ratingToDelete) return;

    try {
      setDeletingRating(true);
      await ratingService.deleteRatingAdmin(ratingToDelete.id);

      // Remove rating from state
      setProductRatings((prev) => {
        const updated = { ...prev };
        if (updated[ratingToDelete.productId]) {
          updated[ratingToDelete.productId] = updated[ratingToDelete.productId].filter(
            (rating) => rating.id !== ratingToDelete.id
          );
        }
        return updated;
      });

      toast.success("Rating deleted successfully");
      setShowDeleteRatingModal(false);
      setRatingToDelete(null);
    } catch (error) {
      console.error("Error deleting rating:", error);
      toast.error(error.response?.data?.message || "Failed to delete rating");
    } finally {
      setDeletingRating(false);
    }
  };

  // Cancel delete rating
  const cancelDeleteRating = () => {
    setShowDeleteRatingModal(false);
    setRatingToDelete(null);
  };

  // Fetch delivery partners
  const fetchDeliveryPartners = async () => {
    try {
      setDeliveryPartnersLoading(true);
      const response = await deliveryProfileService.getDeliveryProfiles({
        is_active: true,
        limit: 100,
      });
      // deliveryProfileService.getDeliveryProfiles already returns response.data
      setDeliveryPartners(response.data || response || []);
    } catch (err) {
      console.error("Error fetching delivery partners:", err);
      toast.error("Failed to fetch delivery partners");
    } finally {
      setDeliveryPartnersLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/admin/orders");
  };

  // Check if order is paid (eligible for invoice)
  const isOrderPaid = () => {
    if (!order) return false;
    const paidStatuses = ["paid", "captured", "completed"];
    return paidStatuses.includes(order.payment_status?.toLowerCase());
  };

  // Handle invoice preview/download
  const handleInvoicePreview = async () => {
    if (!order?.id) return;
    try {
      const blob = await paymentService.downloadInvoice(order.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice opened in new tab");
    } catch (error) {
      console.error("Error previewing invoice:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to preview invoice";
      toast.error(errorMessage);
    }
  };

  // Handle Delhivery / 3PL Packing Slip & Label Print
  const handlePrintPackingSlip = async () => {
    if (!order) return;
    const awb = order.awb_number;
    if (!awb && !order.shipping_label_url) {
      toast.error("No AWB tracking number or shipping label available for this order.");
      return;
    }
    try {
      setLoadingPackingSlip(true);
      let pdfUrl = null;
      const courierPartner = String(order?.courier_partner || "").toLowerCase();
      // If courier is Delhivery or default, fetch real-time packing slip via getPackingSlip
      if (awb && (courierPartner === "delhivery" || !courierPartner || courierPartner.includes("delhivery"))) {
        const cleanAwb = String(awb).split(",")[0].trim();
        try {
          const res = await orderService.getDelhiveryPackingSlip(cleanAwb);
          const pkgData = res?.data?.packages || res?.packages || res?.data?.data?.packages;
          pdfUrl = pkgData?.[0]?.pdf_download_link || res?.data?.pdf_download_link || null;
        } catch (apiErr) {
          console.warn("getDelhiveryPackingSlip API call failed, falling back to stored shipping_label_url if present:", apiErr);
          if (order.shipping_label_url) {
            pdfUrl = order.shipping_label_url;
          } else {
            throw apiErr;
          }
        }
      } else if (order.shipping_label_url) {
        pdfUrl = order.shipping_label_url;
      }
      if (pdfUrl) {
        const newWindow = window.open(pdfUrl, "_blank", "noopener,noreferrer");
        if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        toast.success("Packing slip / label opened in new tab");
      } else {
        toast.error("Packing slip PDF is not yet available or generated for this shipment.");
      }
    } catch (error) {
      console.error("Error fetching packing slip:", error);
      const errMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch packing slip / label";
      toast.error(errMsg);
    } finally {
      setLoadingPackingSlip(false);
    }
  };


  const confirmStatusUpdate = async () => {
    try {
      setActionLoading(true);
      await orderService.updateOrderStatus(id, statusUpdateType);
      await fetchOrderDetails(); // Refresh order details
      await fetchOrderTimeline(); // Refresh timeline
      const statusText = statusUpdateType.replace("_", " ");
      toast.success(`Order status updated to ${statusText}`);
      setShowConfirmModal(false);
      setStatusUpdateType("");
    } catch (err) {
      toast.error("Failed to update order status");
      console.error("Error updating order status:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle order acceptance with delivery partner selection
  const handleAcceptOrder = () => {
    setSelectedDeliveryPartner("");
    fetchDeliveryPartners();
    setShowDeliveryModal(true);
  };

  const handleCancelOrder = () => {
    setCancellationReason("");
    setShowCancelReasonModal(true);
  };

  const handleReturnOrder = () => {
    setActionType("return");
    setShowConfirmModal(true);
  };

  const handleMarkAsPacked = () => {
    setStatusUpdateType("packed");
    setShowConfirmModal(true);
  };

  const handleMarkAsOutForDelivery = () => {
    setStatusUpdateType("out_for_delivery");
    setShowConfirmModal(true);
  };

  const handleMarkAsDelivered = () => {
    setStatusUpdateType("delivered");
    setShowConfirmModal(true);
  };

  // Confirm order acceptance with delivery partner
  const confirmAcceptOrder = async (partnerOverride) => {
    const partner = partnerOverride || selectedDeliveryPartner;
    if (partner) {
      try {
        setActionLoading(true);
        if (partner === "manual") {
          await orderService.updateOrderStatus(id, "accepted", {
            courier_partner: "manual",
            bypass_delivery_partner: true,
          });
          toast.success("Order accepted with Manual / Self Dispatch!");
        } else if (partner === "shadowfax" || partner === "xpressbees" || partner === "delhivery") {
          // Resolve destination pincode
          const pincode = order.shipping_address_details?.pincode ||
            order.shipping_address?.pincode ||
            (order.shipping_address_details && order.shipping_address_details.address_line
              ? String(order.shipping_address_details.address_line).match(/\d{6}/)?.[0]
              : null);

          if (!pincode) {
            toast.error("Could not find a valid 6-digit delivery pincode for this order!");
            return;
          }

          if (partner === "shadowfax") {
            // Check complete order serviceability (customer delivery and all vendor pickups)
            const serviceRes = await orderService.checkOrderServiceability(id);
            if (!serviceRes?.serviceable) {
              toast.error(serviceRes?.message || `Order is not serviceable by Shadowfax! Please select another delivery partner.`);
              return;
            }

            // Trigger Shadowfax 3PL booking directly
            const response = await orderService.bookShipment(id, "shadowfax");
            toast.success(response.message || "Order accepted & Shadowfax shipment booked successfully!");
          } else if (partner === "xpressbees") {
            // Check complete order serviceability via XpressBees
            const serviceRes = await orderService.checkXpressbeesOrderServiceability(id);
            if (!serviceRes?.serviceable) {
              toast.error(serviceRes?.message || `Order is not serviceable by XpressBees! Please select another delivery partner.`);
              return;
            }

            // Trigger XpressBees 3PL booking directly
            const response = await orderService.bookShipment(id, "xpressbees");
            toast.success(response.message || "Order accepted & XpressBees shipment booked successfully!");
          } else if (partner === "delhivery") {
            // Check complete order serviceability via Delhivery
            const serviceRes = await orderService.checkDelhiveryOrderServiceability(id);
            if (!serviceRes?.serviceable) {
              toast.error(serviceRes?.message || `Order is not serviceable by Delhivery! Please select another delivery partner.`);
              return;
            }

            // Trigger Delhivery 3PL booking directly
            const response = await orderService.bookShipment(id, "delhivery");
            toast.success(response.message || "Order accepted & Delhivery shipment booked successfully!");
          }
        } else {
          throw new Error("Delivery partner not Selected");
        }
        await fetchOrderDetails(); // Refresh order details
        await fetchOrderTimeline(); // Refresh timeline
        setShowDeliveryModal(false);
        setSelectedDeliveryPartner("");
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to accept order");
        console.error("Error accepting order:", err);
      } finally {
        setActionLoading(false);
      }
    } else {
      toast.error("Please select a delivery partner or click Bypass");
    }
  };

  // Handle automated 3PL shipment booking
  const handleAutomated3PLBooking = async () => {
    try {
      setBooking3PL(true);
      const response = await orderService.bookShipment(id);
      toast.success(response.message || "3PL Courier shipment booked successfully via Delhivery!");
      await fetchOrderDetails();
      await fetchOrderTimeline();
    } catch (err) {
      console.error("Error booking 3PL shipment:", err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to book 3PL shipment");
    } finally {
      setBooking3PL(false);
    }
  };

  // Handle manual / automated 3PL shipment cancellation
  const handleCancel3PLShipment = async () => {
    if (!order.awb_number) return;
    const reason = window.prompt("Please enter a reason for cancelling this shipment:", "Customer request");
    if (reason === null) return; // User cancelled prompt

    try {
      setBooking3PL(true);
      const cleanAwb = String(order.awb_number).split(",")[0].trim();
      const courierPartner = String(order.courier_partner || "").toLowerCase();
      let response;
      if (courierPartner === "delhivery") {
        response = await orderService.cancelDelhiveryShipment(cleanAwb, reason);
        toast.success(response.message || "Delhivery shipment cancelled successfully!");
      } else if (courierPartner === "xpressbees") {
        response = await orderService.cancelXpressbeesShipment(cleanAwb, reason);
        toast.success(response.message || "XpressBees shipment cancelled successfully!");
      } else {
        response = await orderService.cancelShadowfaxShipment(cleanAwb, reason);
        toast.success(response.message || "Shadowfax shipment cancelled successfully!");
      }
      await fetchOrderDetails();
      await fetchOrderTimeline();
    } catch (err) {
      console.error("Error cancelling shipment:", err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to cancel shipment");
    } finally {
      setBooking3PL(false);
    }
  };

  // Handle manual courier tracking details save
  const handleManualCourierSave = async (e) => {
    e.preventDefault();
    if (!manualAWB.trim() || !manualCourier.trim()) {
      toast.error("Courier name and AWB number are required");
      return;
    }

    try {
      setSavingManual(true);
      // We update the tracking info using order status endpoint, keeping same status
      await orderService.updateOrderStatus(id, order.order_status, {
        awb_number: manualAWB.trim(),
        courier_partner: manualCourier.trim(),
        shipment_id: manualShipmentId.trim() || null,
        tracking_url: manualTrackingUrl.trim() || null,
      });

      toast.success("Manual courier tracking details saved successfully!");
      setShowManualForm(false);
      setManualAWB("");
      setManualShipmentId("");
      await fetchOrderDetails();
      await fetchOrderTimeline();
    } catch (err) {
      console.error("Error saving manual courier info:", err);
      toast.error(err.response?.data?.message || "Failed to save tracking details");
    } finally {
      setSavingManual(false);
    }
  };

  // Confirm order cancellation with reason
  const confirmCancelOrder = async () => {
    if (cancellationReason.trim()) {
      try {
        setActionLoading(true);
        await orderService.cancelOrder(id, {
          reason: cancellationReason.trim(),
        });

        // Mark order as cancelled by current user to prevent duplicate socket toast
        if (window.markOrderCancelledByUser) {
          window.markOrderCancelledByUser(id);
        }

        await fetchOrderDetails(); // Refresh order details
        await fetchOrderTimeline(); // Refresh timeline
        setShowCancelReasonModal(false);
        setCancellationReason("");
        toast.success("Order cancelled successfully");
      } catch (err) {
        toast.error("Failed to cancel order");
        console.error("Error cancelling order:", err);
      } finally {
        setActionLoading(false);
      }
    } else {
      toast.error("Please provide a reason for cancellation");
    }
  };

  const confirmAction = async () => {
    if (actionType === "cancel") {
      try {
        setActionLoading(true);
        await orderService.cancelOrder(id);

        // Mark order as cancelled by current user to prevent duplicate socket toast
        if (window.markOrderCancelledByUser) {
          window.markOrderCancelledByUser(id);
        }

        await fetchOrderDetails(); // Refresh order details
        toast.success("Order cancelled successfully");
        setShowConfirmModal(false);
      } catch (err) {
        toast.error("Failed to cancel order");
        console.error("Error cancelling order:", err);
      } finally {
        setActionLoading(false);
      }
    } else if (actionType === "return") {
      try {
        setActionLoading(true);
        await orderService.updateOrderStatus(id, "returned");
        await fetchOrderDetails();
        await fetchOrderTimeline(); // Refresh order details
        toast.success("Order marked as returned successfully");
        setShowConfirmModal(false);
      } catch (err) {
        toast.error("Failed to mark order as returned");
        console.error("Error marking order as returned:", err);
      } finally {
        setActionLoading(false);
      }
    } else if (statusUpdateType) {
      await confirmStatusUpdate();
    }
  };

  const cancelAction = () => {
    setShowConfirmModal(false);
    setActionType("");
    setStatusUpdateType("");
  };

  // Cancel delivery partner selection
  const cancelDeliverySelection = () => {
    setShowDeliveryModal(false);
    setSelectedDeliveryPartner("");
  };

  // Cancel cancellation reason modal
  const cancelCancellationReason = () => {
    setShowCancelReasonModal(false);
    setCancellationReason("");
  };

  // Helper functions for confirmation modal
  const getConfirmationTitle = () => {
    if (actionType === "return") return "Mark as Returned";
    if (actionType === "cancel") return "Cancel Order";
    if (statusUpdateType === "packed") return "Mark as Packed";
    if (statusUpdateType === "out_for_delivery")
      return "Mark as Out for Delivery";
    if (statusUpdateType === "delivered") return "Mark as Delivered";
    return "Confirm Action";
  };

  const getConfirmationMessage = () => {
    const orderNo = order?.order_no || (order?.id ? `#${order.id.slice(-8)}` : "Unknown");
    if (actionType === "return") {
      return `Are you sure you want to mark order ${orderNo} as returned? This action cannot be undone.`;
    }
    if (actionType === "cancel") {
      return `Are you sure you want to cancel order ${orderNo}? This action cannot be undone.`;
    }
    if (statusUpdateType === "packed") {
      return `Are you sure you want to mark order ${orderNo} as packed? This will update the order status.`;
    }
    if (statusUpdateType === "out_for_delivery") {
      return `Are you sure you want to mark order ${orderNo} as out for delivery? This will update the order status.`;
    }
    if (statusUpdateType === "delivered") {
      return `Are you sure you want to mark order ${orderNo} as delivered? This will complete the order.`;
    }
    return "Are you sure you want to perform this action?";
  };

  const getConfirmationButtonText = () => {
    if (actionType === "return") return "Mark as Returned";
    if (actionType === "cancel") return "Cancel Order";
    if (statusUpdateType === "packed") return "Mark as Packed";
    if (statusUpdateType === "out_for_delivery")
      return "Mark as Out for Delivery";
    if (statusUpdateType === "delivered") return "Mark as Delivered";
    return "Confirm";
  };

  const getCancelButtonText = () => {
    if (actionType === "return") return "Keep Delivered";
    if (actionType === "cancel") return "Keep Order";
    if (statusUpdateType) return "Cancel";
    return "Cancel";
  };

  const getConfirmationVariant = () => {
    if (actionType === "return") return "warning";
    if (actionType === "cancel") return "danger";
    if (statusUpdateType === "delivered") return "success";
    if (statusUpdateType) return "primary";
    return "danger";
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    const key = status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    switch (key) {
      case "placed":
        return "warning";
      case "accepted":
      case "confirmed":
        return "info";
      case "packed":
      case "out_for_delivery":
        return "primary";
      case "delivered":
        return "success";
      case "cancelled":
        return "danger";
      case "returned":
        return "secondary";
      case "refunded":
        return "warning";
      default:
        return "secondary";
    }
  };

  // Get payment status badge variant
  const getPaymentStatusBadgeVariant = (status) => {
    const key = status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    switch (key) {
      case "paid":
      case "captured":
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "refunded":
      case "partially_refunded":
        return "warning";
      case "failed":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Format date with DD-MM-YYYY and 12hr time format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return formatDateTimeToDDMMYYYY12Hr(dateString);
  };



  // Convert status strings like "out_for_delivery" to "Out For Delivery"
  const toTitleCase = (value) => {
    if (!value || typeof value !== "string") return "";
    return value
      .toLowerCase()
      .split(/[_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (loading) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (error || !order || !order.id) {
    return (
      <Container className="view-form">
        <div className="text-center mt-4">
          <h4>Order not found</h4>
          <p className="text-muted">
            {error || "The requested order could not be found."}
          </p>
          <Button variant="primary" onClick={handleBack}>
            Back to Orders
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="view-form">
      <style>{`
        /* Premium Card & Header Theming */
        .card {
          border-radius: 16px !important;
          border: 1px solid rgba(0, 0, 0, 0.05) !important;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02) !important;
          background: #ffffff !important;
          overflow: hidden !important;
          margin-bottom: 24px !important;
        }

        .form-header {
          background-color: #ffffff !important;
          color: #0f172a !important;
          border-bottom: 1px solid #e2e8f0 !important;
          padding: 16px 24px !important;
          border-top-left-radius: 16px !important;
          border-top-right-radius: 16px !important;
        }

        .form-header h5 {
          color: #0f172a !important;
          font-weight: 700 !important;
        }

        .card-header {
          background-color: #f8fafc !important;
          color: #1e293b !important;
          border-bottom: 1px solid #e2e8f0 !important;
          padding: 16px 24px !important;
        }

        .card-body {
          padding: 24px !important;
        }

        .product-img-wrapper {
          border: 2px solid #f1f5f9 !important;
          border-radius: 12px !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
          transition: all 0.3s ease;
        }

        .product-img-wrapper:hover {
          transform: scale(1.08);
          border-color: #F7AD19 !important;
        }

        /* Modernized Elegant Status Pills inside details */
        .badge {
          padding: 7px 13px !important;
          border-radius: 30px !important;
          font-weight: 700 !important;
          font-size: 0.72rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          display: inline-block !important;
          border: 1px solid transparent !important;
        }

        .bg-warning {
          background-color: #fffbeb !important;
          color: #d97706 !important;
          border: 1px solid #fde68a !important;
        }

        .bg-info {
          background-color: #eff6ff !important;
          color: #1d4ed8 !important;
          border: 1px solid #bfdbfe !important;
        }

        .bg-primary {
          background-color: #f5f3ff !important;
          color: #6d28d9 !important;
          border: 1px solid #ddd6fe !important;
        }

        .bg-success {
          background-color: #ecfdf5 !important;
          color: #047857 !important;
          border: 1px solid #a7f3d0 !important;
        }

        .bg-danger {
          background-color: #fef2f2 !important;
          color: #991b1b !important;
          border: 1px solid #fecaca !important;
        }

        .bg-secondary {
          background-color: #f8fafc !important;
          color: #475569 !important;
          border: 1px solid #cbd5e1 !important;
        }

        /* Premium Buttons Matching UI Theme */
        .btn-outline-success {
          background-color: #27ae60 !important;
          border-color: #27ae60 !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          border-radius: 8px !important;
          transition: all 0.2s ease !important;
        }

        .btn-outline-success:hover {
          background-color: #219653 !important;
          border-color: #219653 !important;
          color: #ffffff !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 10px rgba(39, 174, 96, 0.2) !important;
        }

        .btn-outline-info {
          background-color: #17a2b8 !important;
          border-color: #17a2b8 !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          border-radius: 8px !important;
          transition: all 0.2s ease !important;
        }

        .btn-outline-info:hover {
          background-color: #138496 !important;
          border-color: #138496 !important;
          color: #ffffff !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 10px rgba(23, 162, 184, 0.2) !important;
        }

        .btn-outline-danger-custom {
          background-color: #dc3545 !important;
          border-color: #dc3545 !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          border-radius: 8px !important;
          transition: all 0.2s ease !important;
        }

        .btn-outline-danger-custom:hover {
          background-color: #bd2130 !important;
          border-color: #bd2130 !important;
          color: #ffffff !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 10px rgba(220, 53, 69, 0.2) !important;
        }

        .btn-primary {
          background-color: #000000 !important;
          border-color: #000000 !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          border-radius: 8px !important;
          transition: all 0.2s ease !important;
        }

        .btn-primary:hover {
          background-color: #333333 !important;
          border-color: #333333 !important;
          color: #ffffff !important;
          transform: translateY(-1px) !important;
        }

        /* Faded Disabled State Overrides */
        .btn-outline-success:disabled,
        .btn-outline-success.disabled,
        .btn-outline-info:disabled,
        .btn-outline-info.disabled,
        .btn-outline-danger-custom:disabled,
        .btn-outline-danger-custom.disabled,
        .btn-primary:disabled,
        .btn-primary.disabled {
          background-color: #e2e8f0 !important;
          border-color: #cbd5e1 !important;
          color: #94a3b8 !important;
          cursor: not-allowed !important;
          opacity: 0.65 !important;
          box-shadow: none !important;
          transform: none !important;
        }

        /* Animated & Eye-Catchy Timeline */
        .order-timeline {
          position: relative;
          padding-left: 20px;
        }

        .order-timeline::before {
          content: "";
          position: absolute;
          left: 9px;
          top: 0;
          bottom: 0;
          width: 3px;
          background: #cbd5e1;
          border-radius: 2px;
          z-index: 1;
        }

        /* Animated gradient line for active/completed timeline */
        .order-timeline.active::before,
        .order-timeline.completed::before {
          background: linear-gradient(to bottom, #28a745, #F7AD19);
          background-size: 100% 200%;
          animation: lineFlow 3s linear infinite;
        }

        @keyframes lineFlow {
          0% { background-position: 0% 0%; }
          100% { background-position: 0% 100%; }
        }

        .timeline-item {
          position: relative;
          margin-bottom: 25px;
          padding-left: 35px;
          transition: all 0.3s ease;
        }

        .timeline-item:last-child {
          margin-bottom: 0;
        }

        .timeline-item:hover .timeline-content {
          transform: translateX(4px);
          border-color: #F7AD19;
          box-shadow: 0 4px 12px rgba(247, 173, 25, 0.06);
        }

        .timeline-item:hover .timeline-marker,
        .timeline-item:hover .timeline-marker-end {
          transform: scale(1.15);
        }

        .timeline-marker,
        .timeline-marker-end {
          position: absolute;
          left: -25px;
          top: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #f1f5f9;
          border: 3px solid #cbd5e1;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .timeline-marker i,
        .timeline-marker-end i {
          font-size: 11px;
          color: #94a3b8;
        }

        /* Completed Step */
        .timeline-item.completed .timeline-marker,
        .timeline-item.completed .timeline-marker-end {
          background: #28a745;
          border-color: #28a745;
          box-shadow: 0 0 10px rgba(40, 167, 69, 0.2);
        }

        .timeline-item.completed .timeline-marker i,
        .timeline-item.completed .timeline-marker-end i {
          color: #ffffff;
          animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        /* Active Step (Glowing Pulse & Blinking in Orange) */
        .timeline-item.active .timeline-marker,
        .timeline-item.active .timeline-marker-end {
          background: #F7AD19;
          border-color: #ffffff;
          animation: orangePulse 2s infinite, markerBlink 1.2s ease-in-out infinite;
          box-shadow: 0 0 12px rgba(247, 173, 25, 0.6);
        }

        .timeline-item.active .timeline-marker i,
        .timeline-item.active .timeline-marker-end i {
          color: #ffffff;
          animation: iconFloat 2s ease-in-out infinite;
        }

        /* Cancelled Step */
        .timeline-item.cancelled .timeline-marker,
        .timeline-item.cancelled .timeline-marker-end {
          background: #dc3545;
          border-color: #dc3545;
          box-shadow: 0 0 10px rgba(220, 53, 69, 0.2);
        }

        .timeline-item.cancelled .timeline-marker i,
        .timeline-item.cancelled .timeline-marker-end i {
          color: #ffffff;
        }

        /* Returned Step */
        .timeline-item.returned .timeline-marker,
        .timeline-item.returned .timeline-marker-end {
          background: #6c757d;
          border-color: #6c757d;
          box-shadow: 0 0 10px rgba(108, 117, 125, 0.2);
        }

        .timeline-item.returned .timeline-marker i,
        .timeline-item.returned .timeline-marker-end i {
          color: #ffffff;
        }

        .timeline-content {
          margin-top: -2px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 12px 16px;
          transition: all 0.3s ease;
        }

        .timeline-item.active .timeline-content {
          background: #fffdf6;
          border-color: #F7AD19;
        }

        .timeline-title {
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 2px;
          color: #334155;
          line-height: 1.3;
        }

        .timeline-item.completed .timeline-title {
          color: #1e7e34;
        }

        .timeline-item.active .timeline-title {
          color: #d97706;
        }

        .timeline-item.cancelled .timeline-title {
          color: #b91c1c;
        }

        .timeline-item.returned .timeline-title {
          color: #475569;
        }

        .timeline-content small {
          font-size: 11px;
          color: #64748b;
          display: block;
          margin-top: 2px;
        }

        @keyframes orangePulse {
          0% { box-shadow: 0 0 0 0 rgba(247, 173, 25, 0.6); }
          70% { box-shadow: 0 0 0 12px rgba(247, 173, 25, 0); }
          100% { box-shadow: 0 0 0 0 rgba(247, 173, 25, 0); }
        }

        @keyframes markerBlink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.96); }
        }

        @keyframes iconFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }

        @keyframes scaleIn {
          0% { transform: scale(0); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      {/* Delivery Partner Selection Modal */}
      <Modal
        show={showDeliveryModal}
        onHide={cancelDeliverySelection}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Accept Order - Select Delivery Partner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Order Details Section */}
          {order && (
            <div className="mb-4">
              <h6 className="fw-bold text-primary mb-3">Order Details</h6>
              <div
                className="row pt-3 rounded border mb-3 mx-0"
                style={{ backgroundColor: "#f8fafc", color: "#334155" }}
              >
                <div className="col-md-6">
                  <p className="texr-left">
                    <strong>Order Number:</strong> {order?.order_no || order?.id || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Customer Name:</strong>{" "}
                    {order.shipping_address_details?.receiver_name || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Customer Phone:</strong>{" "}
                    {order.customer_phone || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Total Amount:</strong>{" "}
                    <span className="text-success fw-bold">
                      ₹
                      {parseFloat(
                        order.final_amount || order.total_amount || 0
                      ).toFixed(2)}
                    </span>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="texr-left">
                    <strong>Items:</strong> {order.items?.length || 0} item(s)
                  </p>
                  <p className="texr-left">
                    <strong>Order Date:</strong>{" "}
                    {formatDate(order.created_at || new Date())}
                  </p>
                  <p className="texr-left">
                    <strong>Payment Status:</strong>
                    <Badge
                      bg={getPaymentStatusBadgeVariant(order.payment_status)}
                      className="ms-2"
                    >
                      {toTitleCase(order.payment_status || "Unknown")}
                    </Badge>
                  </p>
                </div>
              </div>

              {/* Order Items Preview */}
              {order.items && order.items.length > 0 && (
                <div className="mt-3">
                  <h6 className="fw-bold">Order Items:</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.slice(0, 3).map((item, index) => {
                          const hasDetailedQuantity = item.order_quantity && item.product_quantity;
                          const displayQuantity = hasDetailedQuantity
                            ? `${formatQuantity(item.order_quantity, item.unit)} × ${formatQuantity(item.product_quantity, item.unit)} ${item.unit || ""}`
                            : formatQuantityWithUnit(item.quantity, item.unit);
                          return (
                            <tr key={index}>
                              <td>{item.product_name || "Unknown Product"}</td>
                              <td>{displayQuantity}</td>
                              <td>₹{parseFloat(item.subtotal || item.price || 0).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                        {order.items.length > 3 && (
                          <tr>
                            <td colSpan="3" className="text-center text-muted">
                              +{order.items.length - 3} more items
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <hr className="my-3" />
            </div>
          )}

          <p className="fw-bold texr-left">
            Please select a delivery partner for this order:
          </p>
          {deliveryPartnersLoading ? (
            <div className="text-center">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading delivery partners...</span>
            </div>
          ) : (
            <Form.Group>
              <Form.Label className="fw-semibold">Delivery Partner</Form.Label>
              <Form.Select
                value={selectedDeliveryPartner}
                onChange={(e) => setSelectedDeliveryPartner(e.target.value)}
                required
              >
                <option value="">Select a delivery partner</option>
                <optgroup label="Automated 3PL Couriers">
                  <option value="delhivery">Delhivery (Automated 3PL Courier)</option>
                  <option value="shadowfax">Shadowfax (Automated 3PL Courier)</option>
                  <option value="xpressbees">XpressBees (Automated 3PL Courier)</option>
                </optgroup>
                <optgroup label="Manual / Self Dispatch">
                  <option value="manual">Manual / Self Delivery (Bypass 3PL)</option>
                </optgroup>
              </Form.Select>
              {selectedDeliveryPartner === "manual" && (
                <div className="alert alert-info py-2 px-3 mt-3 mb-0 small text-start d-flex align-items-center gap-2">
                  <i className="fas fa-info-circle text-primary fs-6"></i>
                  <span>
                    <strong>Manual / Self Delivery:</strong> The order will be accepted without generating an automated 3PL shipment. You can assign a rider or enter tracking details manually later.
                  </span>
                </div>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-flex align-items-center gap-1.5"
              onClick={() => confirmAcceptOrder("manual")}
              disabled={actionLoading}
              title="Accept immediately without 3PL courier (Manual/Self Dispatch)"
            >
              <i className="fas fa-forward"></i> Bypass & Accept
            </Button>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-danger-custom text-white"
              onClick={cancelDeliverySelection}
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              className="btn btn-outline-success text-white"
              onClick={() => confirmAcceptOrder()}
              disabled={!selectedDeliveryPartner || actionLoading}
            >
              {actionLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                "Accept Order"
              )}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Cancellation Reason Modal */}
      <Modal
        show={showCancelReasonModal}
        onHide={cancelCancellationReason}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Order Details Section */}
          {order && (
            <div className="mb-4">
              <h6 className="fw-bold text-danger mb-3">Order Details</h6>
              <div
                className="row pt-3 rounded border mb-3 mx-0"
                style={{ backgroundColor: "#f8fafc", color: "#334155" }}
              >
                <div className="col-md-6">
                  <p className="texr-left">
                    <strong>Order Number:</strong> {order?.order_no || order?.id || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Customer Name:</strong>{" "}
                    {order.shipping_address_details?.receiver_name || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Customer Phone:</strong>{" "}
                    {order.customer_phone || "Unknown"}
                  </p>
                  <p className="texr-left">
                    <strong>Total Amount:</strong>{" "}
                    <span className="text-success fw-bold">
                      ₹
                      {parseFloat(
                        order.final_amount || order.total_amount || 0
                      ).toFixed(2)}
                    </span>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="texr-left">
                    <strong>Items:</strong> {order.items?.length || 0} item(s)
                  </p>
                  <p className="texr-left">
                    <strong>Order Date:</strong>{" "}
                    {formatDate(order.created_at || new Date())}
                  </p>
                  <p className="texr-left">
                    <strong>Payment Status:</strong>
                    <Badge
                      bg={getPaymentStatusBadgeVariant(order.payment_status)}
                      className="ms-2"
                    >
                      {toTitleCase(order.payment_status || "Unknown")}
                    </Badge>
                  </p>
                </div>
              </div>

              {/* Order Items Preview */}
              {order.items && order.items.length > 0 && (
                <div className="mt-3">
                  <h6 className="fw-bold">Order Items:</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.slice(0, 3).map((item, index) => {
                          const hasDetailedQuantity = item.order_quantity && item.product_quantity;
                          const displayQuantity = hasDetailedQuantity
                            ? `${formatQuantity(item.order_quantity, item.unit)} × ${formatQuantity(item.product_quantity, item.unit)} ${item.unit || ""}`
                            : formatQuantityWithUnit(item.quantity, item.unit);
                          return (
                            <tr key={index}>
                              <td>{item.product_name || "Unknown Product"}</td>
                              <td>{displayQuantity}</td>
                              <td>₹{parseFloat(item.subtotal || item.price || 0).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                        {order.items.length > 3 && (
                          <tr>
                            <td colSpan="3" className="text-center text-muted">
                              +{order.items.length - 3} more items
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <hr className="my-3" />
            </div>
          )}

          <p className="fw-bold text-danger">
            Please provide a reason for cancelling this order:
          </p>
          <Form.Group>
            <Form.Label>Reason for Cancellation</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="Enter the reason for cancelling this order..."
              required
            />
            <Form.Text className="text-muted">
              This reason will be recorded and may be shared with the customer.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-outline-success text-white"
            onClick={cancelCancellationReason}
          >
            Keep Order
          </button>
          <button
            className="btn btn-outline-danger-custom text-white"
            onClick={confirmCancelOrder}
            disabled={!cancellationReason.trim() || actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Cancelling...
              </>
            ) : (
              "Cancel Order"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={showConfirmModal}
        onHide={cancelAction}
        onConfirm={confirmAction}
        title={getConfirmationTitle()}
        message={getConfirmationMessage()}
        confirmText={getConfirmationButtonText()}
        cancelText={getCancelButtonText()}
        variant={getConfirmationVariant()}
        isLoading={actionLoading}
      />

      {/* Delete Rating Confirmation Modal */}
      <ConfirmModal
        show={showDeleteRatingModal}
        onHide={cancelDeleteRating}
        onConfirm={confirmDeleteRating}
        title="Delete Rating"
        message="Are you sure you want to delete this rating? This action cannot be undone."
        confirmText="Delete Rating"
        cancelText="Cancel"
        variant="danger"
        isLoading={deletingRating}
      />

      {/* Return & Exchange Request Modal */}
      <Modal show={showReturnModal} onHide={() => setShowReturnModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-dark">Request Return / Exchange</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitReturnRequest}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Request Type</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="radio"
                  id="type-return"
                  label="Return (Refund)"
                  name="returnType"
                  value="return"
                  checked={returnType === "return"}
                  onChange={(e) => setReturnType(e.target.value)}
                />
                <Form.Check
                  type="radio"
                  id="type-exchange"
                  label="Exchange (Replacement)"
                  name="returnType"
                  value="exchange"
                  checked={returnType === "exchange"}
                  onChange={(e) => setReturnType(e.target.value)}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Reason for Request</Form.Label>
              <Form.Control
                type="text"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="e.g. Defective item, Wrong size delivered"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Notes (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                placeholder="Add any additional context..."
              />
            </Form.Group>

            {returnType === "return" && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold d-flex align-items-center justify-content-between">
                  <span>UPI ID for Online Refund (Optional)</span>
                  <span className="badge bg-light text-primary border">Online Payout</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={returnUpiId}
                  onChange={(e) => setReturnUpiId(e.target.value)}
                  placeholder="e.g. 9876543210@paytm or username@oksbi"
                />
                <Form.Text className="text-muted small">
                  Enter your UPI ID if you would like to collect your refund online.
                </Form.Text>
              </Form.Group>
            )}

            {/* Upload Damage Proof Images */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold d-flex justify-content-between">
                <span>Upload Damage / Defect Photos (Optional)</span>
                <span className="text-muted small">{returnImages.length}/5 selected</span>
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                onChange={handleReturnImagesChange}
                disabled={returnImages.length >= 5}
              />
              <Form.Text className="text-muted">
                You can upload up to 5 clear photos showing the issue.
              </Form.Text>
              {returnImagePreviews.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {returnImagePreviews.map((preview, idx) => (
                    <div
                      key={idx}
                      className="position-relative border rounded p-1 bg-light shadow-sm"
                      style={{ width: "70px", height: "70px" }}
                    >
                      <img
                        src={preview}
                        alt={`Preview ${idx + 1}`}
                        className="w-100 h-100 rounded"
                        style={{ objectFit: "cover" }}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm position-absolute top-0 end-0 p-0 rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "18px",
                          height: "18px",
                          transform: "translate(30%, -30%)",
                          fontSize: "11px",
                          lineHeight: 1,
                        }}
                        onClick={() => handleRemoveReturnImage(idx)}
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            {/* Upload Proof Videos */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold d-flex justify-content-between">
                <span>Upload Unboxing / Defect Video (Optional)</span>
                <span className="text-muted small">{returnVideos.length}/2 selected</span>
              </Form.Label>
              <Form.Control
                type="file"
                accept="video/*"
                multiple
                onChange={handleReturnVideosChange}
                disabled={returnVideos.length >= 2}
              />
              <Form.Text className="text-muted">
                Upload up to 2 short video clips (MP4, WebM, MOV) showing the unboxing or malfunction.
              </Form.Text>
              {returnVideos.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {returnVideos.map((vid, idx) => (
                    <div
                      key={idx}
                      className="badge bg-light text-dark border p-2 d-flex align-items-center gap-2"
                    >
                      <span className="text-truncate" style={{ maxWidth: "220px" }}>
                        📹 {vid.name}
                      </span>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm p-0 px-1 border-0 fw-bold"
                        onClick={() => handleRemoveReturnVideo(idx)}
                        title="Remove video"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            <h6 className="fw-bold mt-4 mb-2">Select Items to Return / Exchange</h6>
            <Table bordered hover size="sm">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "40px" }} className="text-center">Select</th>
                  <th>Product</th>
                  <th style={{ width: "100px" }} className="text-center">Ordered Qty</th>
                  <th style={{ width: "120px" }} className="text-center">Return Qty</th>
                </tr>
              </thead>
              <tbody>
                {order && order.items && order.items.map((item) => {
                  const selection = selectedReturnItems[item.product_id] || { selected: false, quantity: 1 };
                  return (
                    <tr key={item.product_id} className="align-middle">
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={selection.selected}
                          onChange={() => handleReturnItemToggle(item.product_id)}
                        />
                      </td>
                      <td>
                        <span className="fw-semibold">{item.product_name || "Book Item"}</span>
                      </td>
                      <td className="text-center">{item.quantity}</td>
                      <td>
                        <Form.Control
                          type="number"
                          min="1"
                          max={item.quantity}
                          value={selection.quantity}
                          disabled={!selection.selected}
                          onChange={(e) => handleReturnQtyChange(item.product_id, e.target.value)}
                          size="sm"
                          className="text-center"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowReturnModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submittingReturn}>
              {submittingReturn ? "Submitting..." : "Submit Request"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          <Link to="/admin/orders" className="text-decoration-none">
            Orders
          </Link>
          {"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Order Details
        </Link>
        {isVendor && (
          <Badge bg="info" className="px-3 py-2 text-dark fs-7 fw-semibold shadow-sm">
            <i className="fas fa-store me-1.5"></i> Vendor View (Your Products Only)
          </Badge>
        )}
      </div>

      {isVendor && (
        <div className="alert alert-info py-2.5 px-3 mb-3 d-flex align-items-center justify-content-between shadow-sm border-0 rounded-3">
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-info-circle text-primary fs-5"></i>
            <div>
              <strong className="d-block text-dark">Vendor Order View</strong>
              <small className="text-muted">You are viewing only the items and payout details that belong to your store for this order.</small>
            </div>
          </div>
          <Badge bg="primary" className="px-2.5 py-1.5">Vendor Scoped</Badge>
        </div>
      )}

      <Row>
        <Col lg={9}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">
              Order Details - {order?.order_no || order?.id || "Unknown"}
            </h5>
            <div className="d-flex gap-2">
              {/* <Button variant="outline-light" size="sm" onClick={handleBack}>
                Back
              </Button> */}
            </div>
          </div>
          <Card className="shadow">
            <CardHeader className="d-flex justify-content-between flex-wrap gap-2 align-items-center">
              <div className="d-flex flex-wrap gap-2">
                {/* Approve Order Button - Enabled only for placed orders */}
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={handleAcceptOrder}
                  disabled={order.order_status !== "placed" || actionLoading}
                  title={
                    order.order_status !== "placed"
                      ? "Order already processed"
                      : "Accept Order"
                  }
                >
                  Accept Order
                </Button>

                {/* Mark as Packed Button - Enabled only for accepted orders */}
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={handleMarkAsPacked}
                  disabled={order.order_status !== "accepted" || actionLoading}
                  title={
                    order.order_status !== "accepted"
                      ? "Order must be accepted first"
                      : "Mark as Packed"
                  }
                >
                  Mark as Packed
                </Button>

                {/* Mark as Out for Delivery Button - Enabled only for packed orders */}
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={handleMarkAsOutForDelivery}
                  disabled={order.order_status !== "packed" || actionLoading}
                  title={
                    order.order_status !== "packed"
                      ? "Order must be packed first"
                      : "Mark as Out for Delivery"
                  }
                >
                  Mark as Out for Delivery
                </Button>

                {/* Mark as Delivered Button - Enabled only for out_for_delivery orders */}
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={handleMarkAsDelivered}
                  disabled={
                    order.order_status !== "out_for_delivery" || actionLoading
                  }
                  title={
                    order.order_status !== "out_for_delivery"
                      ? "Order must be out for delivery first"
                      : "Mark as Delivered"
                  }
                >
                  Mark as Delivered
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {/* Invoice Preview Button - Only for paid orders */}
                {isOrderPaid() && (
                  <Button
                    size="sm"
                    variant="outline-info"
                    onClick={handleInvoicePreview}
                    title="Preview Invoice"
                  >
                    <FaFileInvoice /> Invoice
                  </Button>
                )}

                {/* Cancel Order Button - Enabled for all statuses except cancelled, returned and delivered */}
                <Button
                  size="sm"
                  variant="outline-danger-custom"
                  onClick={handleCancelOrder}
                  disabled={
                    ["cancelled", "returned", "delivered"].includes(
                      order.order_status
                    ) || actionLoading
                  }
                  title={
                    ["cancelled", "returned", "delivered"].includes(
                      order.order_status
                    )
                      ? "Order already finalized"
                      : "Cancel Order"
                  }
                >
                  Cancel Order
                </Button>

                {/* Return Request Button */}
                {activeReturnRequest ? (
                  <Link to={`/admin/returns/details/${activeReturnRequest.id}`}>
                    <Button size="sm" variant="info" className="text-white">
                      View Active Return Request ({activeReturnRequest.status.replace("_", " ").toUpperCase()})
                    </Button>
                  </Link>
                ) : (
                  <Button
                    size="sm"
                    variant="outline-success"
                    onClick={handleOpenReturnModal}
                    disabled={order.order_status !== "delivered" || actionLoading}
                    title={order.order_status !== "delivered" ? "Only delivered orders can be returned" : "Return / Exchange"}
                  >
                    Return / Exchange
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardBody>
              {/* Order Information Section */}
              <div className="d-flex align-items-center mb-3 mt-2">
                <div style={{ width: '4px', height: '18px', backgroundColor: '#F7AD19', marginRight: '8px', borderRadius: '2px' }}></div>
                <h6 className="mb-0 fw-bold text-dark text-uppercase tracking-wider" style={{ fontSize: '0.85rem' }}>Order Information</h6>
              </div>
              <Row className="my-2 view-form">
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Order Number</label>
                  <div style={{ width: "240px" }}>
                    <p
                      className="mb-0 text-primary fw-bold"
                      style={{
                        textAlign: "left",
                        paddingBottom: "8px",
                      }}
                    >
                      {order?.order_no || `#${order?.id || "Unknown"}`}
                    </p>
                  </div>
                  <p
                    className="mb-0"
                    style={{
                      borderBottom: "1px solid #ccc",
                    }}
                  ></p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Order Status</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "25px",
                    }}
                  >
                    <Badge bg={getStatusBadgeVariant(order.order_status)}>
                      {toTitleCase(order.order_status || "Unknown")}
                    </Badge>
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Payment Status</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "25px",
                    }}
                  >
                    <Badge
                      bg={getPaymentStatusBadgeVariant(order.payment_status)}
                    >
                      {toTitleCase(order.payment_status || "Unknown")}
                    </Badge>
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">{isVendor ? "Your Items Amount" : "Total Amount"}</label>
                  <p
                    className="mb-0 fw-bold text-success"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    ₹
                    {parseFloat(
                      (isVendor ? (order.vendor_summary?.items_subtotal || order.vendor_subtotal) : (order.final_amount || order.total_amount)) || 0
                    ).toFixed(2)}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Order Date</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {formatDate(order.created_at || new Date())}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Payment Method</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {order.payment_method || "Not specified"}
                  </p>
                </Col>
              </Row>

              {/* Fulfillment & Delivery Section */}
              <div className="d-flex align-items-center mb-3 mt-4">
                <div style={{ width: '4px', height: '18px', backgroundColor: '#F7AD19', marginRight: '8px', borderRadius: '2px' }}></div>
                <h6 className="mb-0 fw-bold text-dark text-uppercase tracking-wider" style={{ fontSize: '0.85rem' }}>Fulfillment & Delivery Details</h6>
              </div>
              <Row className="my-2 view-form">
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Customer</label>
                  <div
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    <Row>
                      <Col
                        md={6}
                        style={{
                          paddingLeft: "0px",
                        }}
                      >
                        <p
                          style={{
                            textAlign: "left",
                            display: "flex",
                            gap: "10px",
                            alignItems: "baseline",
                            marginBottom: "0px",
                          }}
                        >
                          <span className="fw-semibold text-primary">
                            {/* <strong>Phone:</strong>{" "} */}
                            {order.shipping_address_details.receiver_name ||
                              "Unknown"}
                          </span>
                          <small className="text-muted d-block">
                            Phone:
                            {order.customer_phone || "Unknown"}
                          </small>
                        </p>
                      </Col>
                    </Row>
                  </div>
                </Col>

                {/* <Col lg={4} className="my-2">
                  <label className="fw-bold">Customer Phone</label>
                  <div
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    <Row>
                      <Col
                        md={6}
                        style={{
                          paddingLeft: "0px",
                        }}
                      >
                        <p className="mb-2 text-start">
                          {/* <strong>Phone:</strong>{" "} */}
                {/* {order.customer_phone || "Unknown"}
                        </p>
                      </Col>
                    </Row>
                  </div> */}
                {/* </Col>  */}
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Delivery Partner</label>

                  {/* Always render the block so fallback can show */}
                  <div>
                    <p
                      className="mb-2"
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #ccc",
                        paddingBottom: "5px",
                        display: "flex",
                        gap: "10px",
                        alignItems: "baseline",
                      }}
                    >
                      {/* When BOTH name & phone are missing -> show fallback */}
                      {!order?.delivery_boy_name &&
                        !order?.delivery_boy_phone ? (
                        <span className="text-muted">N/A</span>
                      ) : (
                        <>
                          <span className="fw-semibold text-primary">
                            {order.delivery_boy_name || "N/A"}
                          </span>

                          <small className="text-muted d-block">
                            Phone: {order.delivery_boy_phone || "N/A"}
                          </small>
                        </>
                      )}
                    </p>
                  </div>
                </Col>
              </Row>

              {/* Shipping Address Section */}
              <div className="d-flex align-items-center mb-3 mt-4">
                <div style={{ width: '4px', height: '18px', backgroundColor: '#F7AD19', marginRight: '8px', borderRadius: '2px' }}></div>
                <h6 className="mb-0 fw-bold text-dark text-uppercase tracking-wider" style={{ fontSize: '0.85rem' }}>Shipping Address</h6>
              </div>
              <Row className="mx-3 my-2 view-form">
                <Col lg={12} className="my-2 d-flex gap-2">
                  <label className="fw-bold">Address</label>
                  <div
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      paddingBottom: "5px",
                      minHeight: "40px",
                    }}
                  >
                    {order.shipping_address_details ? (
                      <p className="mb-0">
                        {order.shipping_address_details.address_line}
                        {order.shipping_address_details.landmark &&
                          `, ${order.shipping_address_details.landmark}`}
                        {`, ${order.shipping_address_details.city}, ${order.shipping_address_details.state} - ${order.shipping_address_details.pincode}`}
                        {order.shipping_address_details.receiver_name &&
                          ` (Receiver: ${order.shipping_address_details.receiver_name})`}
                        {order.shipping_address_details.receiver_phone &&
                          ` (Phone: ${order.shipping_address_details.receiver_phone})`}
                      </p>
                    ) : (
                      <p className="mb-0">
                        {order.shipping_address ||
                          "No shipping address provided"}
                      </p>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3}>
          <Card className="shadow">
            <CardHeader className="form-header shadow-none">
              <h5 className="mb-0 fw-semibold pb-1 pt-1 ">
                Order Status Timeline
              </h5>
            </CardHeader>
            <CardBody>
              <div
                className={`order-timeline ${order.order_status === "delivered"
                  ? "completed"
                  : order.order_status === "cancelled"
                    ? "cancelled"
                    : order.order_status === "returned"
                      ? "returned"
                      : "active"
                  }`}
              >
                {(() => {
                  // If we have timeline data from API, use it
                  if (timeline && timeline.length > 0) {
                    const statusConfig = {
                      placed: {
                        title: "Order Placed",
                        icon: "fas fa-shopping-cart",
                      },
                      accepted: {
                        title: "Order Accepted",
                        icon: "fas fa-check-circle",
                      },
                      packed: { title: "Packed", icon: "fas fa-box" },
                      out_for_delivery: {
                        title: "Out for Delivery",
                        icon: "fas fa-truck",
                      },
                      delivered: { title: "Delivered", icon: "fas fa-home" },
                      cancelled: {
                        title: "Cancelled",
                        icon: "fas fa-times-circle",
                      },
                      returned: { title: "Returned", icon: "fas fa-undo" },
                    };

                    return timeline.map((entry, index) => {
                      const config = statusConfig[entry.status] || {
                        title: entry.status,
                        icon: "fas fa-circle",
                      };
                      const isActive = entry.status === order.order_status;
                      const isCompleted =
                        index < timeline.length - 1 ||
                        ["cancelled", "returned"].includes(entry.status);
                      const itemClass = `timeline-item ${isActive ? "active" : isCompleted ? "completed" : ""
                        } ${entry.status === "cancelled"
                          ? "cancelled"
                          : entry.status === "returned"
                            ? "returned"
                            : ""
                        }`;

                      return (
                        <div key={entry.id} className={itemClass}>
                          <div
                            className={`${index === timeline.length - 1
                              ? "timeline-marker-end"
                              : "timeline-marker"
                              }`}
                          >
                            <i className={config.icon}></i>
                          </div>
                          <div className="timeline-content">
                            <h6 className="timeline-title">{config.title}</h6>
                            <small className="text-muted">
                              {formatDate(entry.changed_at)}
                            </small>
                          </div>
                        </div>
                      );
                    });
                  }

                  // Fallback to static timeline if API data not available
                  const steps = [
                    {
                      key: "placed",
                      title: "Order Placed",
                      icon: "fas fa-shopping-cart",
                      subtitle: order.created_at
                        ? formatDate(order.created_at)
                        : "N/A",
                    },
                    {
                      key: "accepted",
                      title: "Order Accepted",
                      icon: "fas fa-check-circle",
                      subtitle: order.delivery_boy_id
                        ? "Delivery partner assigned"
                        : "Pending acceptance",
                    },
                    {
                      key: "packed",
                      title: "Packed",
                      icon: "fas fa-box",
                      subtitle: "Order packed and ready",
                    },
                    {
                      key: "out_for_delivery",
                      title: "Out for Delivery",
                      icon: "fas fa-truck",
                      subtitle: "On the way to you",
                    },
                    {
                      key: "delivered",
                      title: "Delivered",
                      icon: "fas fa-home",
                      subtitle: "Order delivered successfully",
                    },
                  ];
                  const orderFlow = steps.map((s) => s.key);
                  const currentIndex = orderFlow.indexOf(order.order_status);
                  return (
                    <>
                      {steps.map((step, index) => {
                        const isActive = order.order_status === step.key;
                        const isCompleted =
                          currentIndex > index ||
                          (["cancelled", "returned"].includes(
                            order.order_status
                          ) &&
                            orderFlow.indexOf(step.key) < currentIndex);
                        const itemClass = `timeline-item ${isActive ? "active" : isCompleted ? "completed" : ""
                          }`;
                        return (
                          <div key={step.key} className={itemClass}>
                            <div className="timeline-marker">
                              <i className={step.icon}></i>
                            </div>
                            <div className="timeline-content">
                              <h6 className="timeline-title">{step.title}</h6>
                              <small className="text-muted">
                                {step.subtitle}
                              </small>
                            </div>
                          </div>
                        );
                      })}
                      {order.order_status === "cancelled" && (
                        <div className="timeline-item cancelled">
                          <div className="timeline-marker">
                            <i className="fas fa-times-circle"></i>
                          </div>
                          <div className="timeline-content">
                            <h6 className="timeline-title">Cancelled</h6>
                            <small className="text-muted">
                              {order.cancellation_reason || "Order cancelled"}
                            </small>
                          </div>
                        </div>
                      )}
                      {order.order_status === "returned" && (
                        <div className="timeline-item returned">
                          <div className="timeline-marker">
                            <i className="fas fa-undo"></i>
                          </div>
                          <div className="timeline-content">
                            <h6 className="timeline-title">Returned</h6>
                            <small className="text-muted">Order returned</small>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </CardBody>
          </Card>

          {/* Shipping & 3PL Logistics Card */}
          <Card className="shadow mt-4">
            <CardHeader className="form-header shadow-none bg-primary text-white">
              <h5 className="mb-0 fw-semibold pb-1 pt-1 d-flex align-items-center gap-2">
                <i className="fas fa-truck"></i> Shipping & 3PL
              </h5>
            </CardHeader>
            <CardBody>
              {order.awb_number ? (
                <div className="shipping-info">
                  <div className="mb-3 text-start">
                    <label className="fw-bold text-muted small uppercase d-block">Courier Partner</label>
                    <span className="fw-bold text-dark fs-6">
                      <span className="badge bg-info text-dark px-2.5 py-1.5 rounded-pill fs-7">
                        {order.courier_partner || "Delhivery"}
                      </span>
                    </span>
                  </div>
                  <div className="mb-3 text-start">
                    <label className="fw-bold text-muted small uppercase d-block">AWB Tracking Number</label>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <code className="text-primary fw-bold fs-6 bg-light px-2 py-1 rounded border">
                        {order.awb_number}
                      </code>
                      <Button
                        size="xs"
                        variant="outline-secondary"
                        style={{ padding: "2px 6px", fontSize: "11px" }}
                        onClick={() => {
                          navigator.clipboard.writeText(order.awb_number);
                          toast.success("AWB copied to clipboard!");
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                  {order.shipment_id && (
                    <div className="mb-3 text-start">
                      <label className="fw-bold text-muted small uppercase d-block">Shipment ID</label>
                      <span className="text-secondary small">{order.shipment_id}</span>
                    </div>
                  )}

                  <div className="mt-4 d-flex flex-column gap-2 w-100">
                    <Button
                      className="fw-bold shadow-sm"
                      style={{ fontSize: '0.85rem' }}
                      onClick={handlePrintPackingSlip}
                      disabled={loadingPackingSlip}
                      title="Print Packing Slip / Shipping Label"
                    >
                      {loadingPackingSlip ? (
                        <>
                          <Spinner animation="border" size="sm" />
                          Loading Label...
                        </>
                      ) : (
                        <>
                          <FaPrint /> Print Packing Slip
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="mt-4 d-flex gap-2">
                    {order.tracking_url && (
                      <a
                        href={order.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary w-100 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm py-2 text-white text-decoration-none"
                        style={{ fontSize: '0.85rem' }}
                      >
                        <i className="fas fa-search-location"></i> Track
                      </a>
                    )}


                    {(["shadowfax", "xpressbees", "delhivery"].includes(String(order.courier_partner).toLowerCase())) && (
                      <Button
                        variant="danger"
                        className="w-100 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm py-2 text-white"
                        style={{ fontSize: '0.85rem' }}
                        onClick={handleCancel3PLShipment}
                        disabled={booking3PL}
                      >
                        {booking3PL ? (
                          <>
                            <Spinner animation="border" size="sm" />
                            Wait...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-times-circle"></i> Cancel AWB
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Real-time Tracking Status & Timeline */}
                  <hr className="my-4" />
                  <div className="mb-3 text-start">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="fw-bold text-muted small uppercase mb-0">Real-time Tracker</label>
                      <Button
                        size="xs"
                        variant="link"
                        className="p-0 text-decoration-none text-primary fw-bold"
                        style={{ fontSize: "12px" }}
                        onClick={() => fetchShadowfaxTracking(order.awb_number)}
                        disabled={loadingTracking}
                      >
                        <i className={`fas fa-sync-alt ${loadingTracking ? 'fa-spin' : ''}`}></i> Refresh
                      </Button>
                    </div>

                    {loadingTracking && (
                      <div className="text-center py-3 text-muted small">
                        <Spinner animation="border" size="sm" className="me-2 text-primary" />
                        Fetching real-time tracking...
                      </div>
                    )}

                    {trackingError && !loadingTracking && (
                      <div className="alert alert-warning py-2 px-3 small my-2 d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                        <span>{trackingError}</span>
                      </div>
                    )}

                    {!loadingTracking && !shadowfaxTracking && (
                      <div className="text-center py-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="w-100 fw-bold"
                          onClick={() => fetchShadowfaxTracking(order.awb_number)}
                        >
                          <i className="fas fa-search-location"></i> Fetch Real-time Tracking
                        </Button>
                      </div>
                    )}

                    {!loadingTracking && shadowfaxTracking && (
                      <div className="shadowfax-timeline mt-2">
                        <div className="mb-3 bg-light p-2.5 rounded border">
                          <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>Current Status:</span>
                          <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>
                            {shadowfaxTracking.ShipmentData?.[0]?.Shipment?.Status?.Status ||
                              shadowfaxTracking.status_display ||
                              shadowfaxTracking.status ||
                              shadowfaxTracking.order_details?.status_display ||
                              shadowfaxTracking.order_details?.status ||
                              "In Transit"}
                          </span>
                          {(shadowfaxTracking.ShipmentData?.[0]?.Shipment?.ExpectedDeliveryDate || shadowfaxTracking.order_details?.promised_delivery_date) && (
                            <span className="small text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
                              Expected by: {new Date(shadowfaxTracking.ShipmentData?.[0]?.Shipment?.ExpectedDeliveryDate || shadowfaxTracking.order_details.promised_delivery_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        {/* Timeline list */}
                        <div className="position-relative ps-3 border-start ms-2" style={{ borderColor: '#ddd' }}>
                          {(() => {
                            // 1. Delhivery scans structure
                            const delhiveryShipment = shadowfaxTracking.ShipmentData?.[0]?.Shipment;
                            if (delhiveryShipment) {
                              const dStatus = delhiveryShipment.Status?.Status || "In Transit";
                              const dInstructions = delhiveryShipment.Status?.Instructions;
                              const dScans = delhiveryShipment.Scans || [];

                              if (dScans.length === 0) {
                                return (
                                  <div className="position-relative mb-3">
                                    <div className="position-absolute bg-primary rounded-circle" style={{ width: '8px', height: '8px', left: '-19px', top: '6px' }}></div>
                                    <span className="small fw-semibold d-block text-dark" style={{ fontSize: '0.8rem' }}>{dStatus}</span>
                                    {dInstructions && <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>{dInstructions}</span>}
                                  </div>
                                );
                              }

                              return dScans.map((sc, idx) => {
                                const detail = sc.ScanDetail || sc;
                                const timestamp = detail.ScanDateTime;
                                const formattedTime = timestamp ? new Date(timestamp).toLocaleString() : '';
                                return (
                                  <div key={idx} className="position-relative mb-3">
                                    <div
                                      className={`position-absolute rounded-circle ${idx === 0 ? 'bg-success' : 'bg-secondary'}`}
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        left: '-20px',
                                        top: '6px',
                                        border: '2px solid white',
                                        boxShadow: idx === 0 ? '0 0 0 2px rgba(40, 167, 69, 0.4)' : 'none'
                                      }}
                                    ></div>
                                    <span className={`small fw-bold d-block ${idx === 0 ? 'text-success' : 'text-dark'}`} style={{ fontSize: '0.8rem' }}>
                                      {detail.Scan || detail.Instructions || "Status Update"}
                                    </span>
                                    {detail.ScannedLocation && (
                                      <span className="small text-muted d-block fw-semibold" style={{ fontSize: '0.75rem' }}>
                                        <i className="fas fa-map-marker-alt text-danger me-1"></i>
                                        {detail.ScannedLocation}
                                      </span>
                                    )}
                                    {detail.Instructions && detail.Instructions !== detail.Scan && (
                                      <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>
                                        {detail.Instructions}
                                      </span>
                                    )}
                                    {formattedTime && (
                                      <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                                        <i className="far fa-clock me-1"></i>
                                        {formattedTime}
                                      </span>
                                    )}
                                  </div>
                                );
                              });
                            }

                            // 2. Existing Shadowfax / XpressBees events structure
                            const events = shadowfaxTracking.order_details?.tracking_details ||
                              shadowfaxTracking.tracking_details ||
                              shadowfaxTracking.pickup_request_state_histories ||
                              shadowfaxTracking.delivery_request_state_histories ||
                              [];

                            if (events.length === 0) {
                              return (
                                <div className="position-relative mb-3">
                                  <div className="position-absolute bg-primary rounded-circle" style={{ width: '8px', height: '8px', left: '-19px', top: '6px' }}></div>
                                  <span className="small fw-semibold d-block text-dark" style={{ fontSize: '0.8rem' }}>Shipment Registered</span>
                                  <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>Awaiting pickup by courier partner</span>
                                </div>
                              );
                            }

                            return events.map((ev, idx) => {
                              const timestamp = ev.event_timestamp || ev.timestamp || ev.created_at;
                              const formattedTime = timestamp ? new Date(timestamp).toLocaleString() : '';
                              return (
                                <div key={idx} className="position-relative mb-3">
                                  <div
                                    className={`position-absolute rounded-circle ${idx === 0 ? 'bg-success' : 'bg-secondary'}`}
                                    style={{
                                      width: '8px',
                                      height: '8px',
                                      left: '-20px',
                                      top: '6px',
                                      border: '2px solid white',
                                      boxShadow: idx === 0 ? '0 0 0 2px rgba(40, 167, 69, 0.4)' : 'none'
                                    }}
                                  ></div>
                                  <span className={`small fw-bold d-block ${idx === 0 ? 'text-success' : 'text-dark'}`} style={{ fontSize: '0.8rem' }}>
                                    {ev.status || ev.state || ev.activity}
                                  </span>
                                  {(ev.current_location || ev.location) && (
                                    <span className="small text-muted d-block fw-semibold" style={{ fontSize: '0.75rem' }}>
                                      <i className="fas fa-map-marker-alt text-danger me-1"></i>
                                      {ev.current_location || ev.location}
                                    </span>
                                  )}
                                  {(ev.comments || ev.comment || ev.remarks) && (
                                    <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>
                                      {ev.comments || ev.comment || ev.remarks}
                                    </span>
                                  )}
                                  {formattedTime && (
                                    <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                                      {formattedTime}
                                    </span>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-shipping-info text-center py-2">
                  <div className="text-muted mb-3 small">
                    {order.courier_partner === "manual" ? (
                      <span className="badge bg-secondary text-white px-2 py-1 rounded">
                        <i className="fas fa-truck me-1"></i> Manual / Self Dispatch
                      </span>
                    ) : (
                      "No 3PL shipment has been created for this order yet."
                    )}
                  </div>

                  {["accepted", "packed"].includes(order.order_status) ? (
                    <div className="d-flex flex-column gap-2">
                      <Button
                        variant="primary"
                        className="w-100 fw-bold py-2 d-flex align-items-center justify-content-center gap-2 text-white"
                        onClick={handleAutomated3PLBooking}
                        disabled={booking3PL}
                      >
                        {booking3PL ? (
                          <>
                            <Spinner animation="border" size="sm" />
                            Booking...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-magic"></i> Book 3PL Shipment
                          </>
                        )}
                      </Button>

                      <div className="text-center my-1 text-muted small">- or -</div>

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="w-100"
                        onClick={() => setShowManualForm(!showManualForm)}
                      >
                        {showManualForm ? "Hide Manual Form" : "Enter Manual Tracking"}
                      </Button>

                      {showManualForm && (
                        <Form onSubmit={handleManualCourierSave} className="mt-3 text-start bg-light p-3 rounded border">
                          <h6 className="fw-bold text-dark border-bottom pb-1.5 mb-2.5">Manual Courier</h6>
                          <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold">Courier Name</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={manualCourier}
                              onChange={(e) => setManualCourier(e.target.value)}
                              placeholder="e.g. Delhivery, Ekart"
                              required
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold">AWB Number</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={manualAWB}
                              onChange={(e) => setManualAWB(e.target.value)}
                              placeholder="AWB Tracking #"
                              required
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label className="small fw-bold">Tracking URL</Form.Label>
                            <Form.Control
                              type="url"
                              size="sm"
                              value={manualTrackingUrl}
                              onChange={(e) => setManualTrackingUrl(e.target.value)}
                              placeholder="https://track..."
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Shipment ID (Optional)</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={manualShipmentId}
                              onChange={(e) => setManualShipmentId(e.target.value)}
                              placeholder="Shipment ID"
                            />
                          </Form.Group>
                          <Button
                            type="submit"
                            variant="success"
                            size="sm"
                            className="w-100 fw-bold text-white"
                            disabled={savingManual}
                          >
                            {savingManual ? "Saving..." : "Save Tracking Info"}
                          </Button>
                        </Form>
                      )}
                    </div>
                  ) : (
                    <Badge bg="secondary" className="w-100 py-2 fs-7 font-normal">
                      Courier Booking Pending
                    </Badge>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Order Items Section */}
      <Row className="mt-4">
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">
              Order Items ({order.items?.length || 0})
            </h5>
          </div>
          <Card className="shadow">
            <CardBody>
              {!order.items || order.items.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-shopping-cart fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No Items Found</h5>
                  <p className="text-muted">
                    This order doesn't have any items.
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Product Name</th>
                        <th>Quantity Details</th>
                        <th>Base Price (Vendor)</th>
                        <th className="text-center">Tax (GST)</th>
                        <th className="text-center">Platform Fee</th>
                        <th>Selling Price (Customer)</th>
                        <th>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, index) => {
                        // Display quantity information clearly
                        // If order_quantity and product_quantity exist, show detailed breakdown
                        // Otherwise, show total quantity
                        const hasDetailedQuantity = item.order_quantity && item.product_quantity;
                        const displayQuantity = hasDetailedQuantity
                          ? formatDetailedQuantity(item.order_quantity, item.product_quantity, item.quantity, item.unit)
                          : formatQuantityWithUnit(item.quantity, item.unit);

                        const totalQty = parseFloat(item.quantity || 1);
                        // Selling Price per unit (Snapshot from order - already GST-inclusive)
                        const sellingPricePerUnit = parseFloat(item.price || 0);

                        // Subtotal from database
                        const itemSubtotal = parseFloat(item.subtotal || (totalQty * sellingPricePerUnit));
                        const itemGstPct = parseFloat(item.gst_percentage || 0);
                        const itemGstAmt = parseFloat(item.gst_amount || 0);
                        const itemCommission = parseFloat(item.commission_amount || 0);

                        // Base product price per unit (Excl. GST)
                        const basePricePerUnit = item.product_price !== undefined && item.product_price !== null && parseFloat(item.product_price) > 0
                          ? parseFloat(item.product_price)
                          : (item.gst_amount !== undefined && parseFloat(item.gst_amount) > 0
                            ? parseFloat((sellingPricePerUnit - (parseFloat(item.gst_amount) / (totalQty || 1)) - (itemCommission / (totalQty || 1))).toFixed(2))
                            : (itemGstPct > 0 ? parseFloat((sellingPricePerUnit / (1 + itemGstPct / 100)).toFixed(2)) : sellingPricePerUnit));

                        return (
                          <tr key={index} className="align-middle">
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <div className="product-img-wrapper" style={{ width: "50px", height: "50px", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0", background: "#f8fafc", flexShrink: 0 }}>
                                  {item.image_urls && item.image_urls.length > 0 ? (
                                    <img
                                      src={item.image_urls[0].startsWith("http") ? item.image_urls[0] : `${process.env.REACT_APP_API_URL || ""}${item.image_urls[0]}`}
                                      alt={item.product_name}
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "placeholder-image-url";
                                        e.target.parentNode.innerHTML = '<div class="d-flex align-items-center justify-content-center h-100 text-muted" style="font-size: 16px;"><i class="fas fa-book"></i></div>';
                                      }}
                                    />
                                  ) : (
                                    <div className="d-flex align-items-center justify-content-center h-100 text-muted" style={{ fontSize: "16px" }}>
                                      <i className="fas fa-book"></i>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="fw-bold text-dark">
                                    {item.product_name || "Unknown Product"}
                                  </div>
                                  <small className="text-muted d-block" style={{ fontSize: "11px" }}>
                                    ID: {item.product_id}
                                  </small>
                                  {item.vendor_id && (
                                    <Badge bg="secondary" className="mt-1" style={{ fontSize: "10px", fontWeight: "normal", background: "#475569" }}>
                                      🏢 Vendor ID: {item.vendor_id.slice(-8)}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <Badge bg="primary" className="mb-1">
                                  {displayQuantity}
                                </Badge>
                                {hasDetailedQuantity && (
                                  <div className="small text-muted mt-1">
                                    {formatQuantity(item.order_quantity, item.unit)} unit(s) of {formatQuantity(item.product_quantity, item.unit)} {item.unit} tier
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="text-muted fw-semibold">
                              ₹{basePricePerUnit.toFixed(2)}
                            </td>
                            <td className="text-center">
                              <Badge bg={itemGstPct > 0 ? "info" : "light"} className={itemGstPct > 0 ? "" : "text-muted border"}>
                                {itemGstPct > 0 ? `${itemGstPct}% GST` : "0% (Exempt)"}
                              </Badge>
                              {itemGstAmt > 0 && (
                                <div className="small text-muted mt-1" style={{ fontSize: "11px" }}>
                                  ₹{itemGstAmt.toFixed(2)} total
                                </div>
                              )}
                            </td>
                            <td className="text-center">
                              {itemCommission > 0 ? (
                                <div>
                                  <Badge bg="light" className="text-dark border" style={{ fontSize: "11px" }}>
                                    ₹{itemCommission.toFixed(2)}
                                  </Badge>
                                  {totalQty > 1 && (
                                    <div className="small text-muted mt-1" style={{ fontSize: "10px" }}>
                                      ₹{(itemCommission * totalQty).toFixed(2)} total
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted" style={{ fontSize: "12px" }}>₹0.00</span>
                              )}
                            </td>
                            <td className="fw-bold text-dark">
                              ₹{sellingPricePerUnit.toFixed(2)}
                              <span className="badge bg-light text-success border ms-1" style={{ fontSize: "10px", fontWeight: "normal" }}>
                                Inc. GST
                              </span>
                              {hasDetailedQuantity && (
                                <div className="small text-muted">
                                  per {formatQuantity(item.product_quantity, item.unit)} {item.unit}
                                </div>
                              )}
                            </td>
                            <td className="fw-bold text-success">
                              ₹{itemSubtotal.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      {(() => {
                        // Calculate subtotal from order items (inclusive of GST)
                        const itemsSubtotal =
                          order.items?.reduce((sum, item) => {
                            return sum + parseFloat(item.subtotal || 0);
                          }, 0) || parseFloat(order.total_amount || 0);

                        // Total GST already embedded within the items
                        const totalGstIncluded =
                          order.items?.reduce((sum, item) => {
                            return sum + parseFloat(item.gst_amount || 0);
                          }, 0) || 0;

                        // Total Platform Commission embedded within the items
                        const totalCommissionIncluded =
                          order.items?.reduce((sum, item) => {
                            return sum + (parseFloat(item.commission_amount || 0) * parseFloat(item.quantity || 1));
                          }, 0) || 0;

                        const discountAmount = parseFloat(order.discount_amount || 0);
                        const deliveryCharge = parseFloat(order.delivery_charge || 0);
                        const taxPct = parseFloat(order.tax_percentage || 0);
                        const additionalTaxAmount = taxPct > 0
                          ? parseFloat((((Math.max(0, itemsSubtotal - discountAmount)) * taxPct) / 100).toFixed(2))
                          : 0;

                        const finalAmount = parseFloat(
                          order.final_amount || (itemsSubtotal - discountAmount + deliveryCharge + additionalTaxAmount)
                        );

                        return (
                          <>
                            <tr>
                              <td colSpan="6" className="text-end fw-bold">
                                Items Subtotal (Inc. GST & Fee):
                              </td>
                              <td className="fw-bold">
                                ₹{itemsSubtotal.toFixed(2)}
                              </td>
                            </tr>
                            {discountAmount > 0 && (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="text-end text-muted"
                                >
                                  Discount / Coupon:
                                </td>
                                <td className="text-danger fw-bold">
                                  -₹{discountAmount.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            {deliveryCharge > 0 && (
                              <tr>
                                <td colSpan="6" className="text-end text-muted">
                                  Delivery Charge:
                                </td>
                                <td className="fw-bold">
                                  ₹{deliveryCharge.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            {additionalTaxAmount > 0 && (
                              <tr>
                                <td colSpan="6" className="text-end text-muted">
                                  Additional Tax ({taxPct.toFixed(2)}%):
                                </td>
                                <td className="fw-bold text-dark">
                                  +₹{additionalTaxAmount.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            {totalGstIncluded > 0 && (
                              <tr className="bg-light">
                                <td colSpan="6" className="text-end text-muted small">
                                  <span className="badge bg-light text-secondary border me-1">Tax Info</span>
                                  GST (Already included in items above):
                                </td>
                                <td className="text-muted small fw-semibold">
                                  ₹{totalGstIncluded.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            {totalCommissionIncluded > 0 && (
                              <tr className="bg-light">
                                <td colSpan="6" className="text-end text-muted small">
                                  <span className="badge bg-light text-secondary border me-1">Fee Info</span>
                                  Platform Commission (Included in items above):
                                </td>
                                <td className="text-muted small fw-semibold">
                                  ₹{totalCommissionIncluded.toFixed(2)}
                                </td>
                              </tr>
                            )}

                            {isVendor ? (
                              <>
                                <tr className="table-active">
                                  <th colSpan="6" className="text-end">
                                    Net Vendor Payout (After Commission):
                                  </th>
                                  <th className="text-success fw-bold fs-5">
                                    ₹{Math.max(0, itemsSubtotal - totalCommissionIncluded).toFixed(2)}
                                  </th>
                                </tr>
                                <tr className="bg-light">
                                  <td colSpan="7" className="text-end text-muted small py-1">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Customer Order Total across all items: ₹{parseFloat(order.final_amount || 0).toFixed(2)}
                                  </td>
                                </tr>
                              </>
                            ) : (
                              <tr className="table-active">
                                <th colSpan="6" className="text-end">
                                  Final Amount (Paid):
                                </th>
                                <th className="text-success fw-bold fs-5">
                                  ₹{finalAmount.toFixed(2)}
                                </th>
                              </tr>
                            )}
                          </>
                        );
                      })()}
                    </tfoot>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Customer Ratings & Feedback Section */}
      {order && order.items && order.items.length > 0 && (
        <Row className="mt-4">
          <Col lg={12}>
            <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
              <h5 className="mb-0 fw-semibold text-light">
                Customer Ratings & Feedback
              </h5>
            </div>
            <Card className="shadow">
              <CardBody>
                {order.order_status !== "delivered" &&
                  order.order_status !== "completed" && (
                    <div className="alert alert-info mb-3">
                      <i className="fas fa-info-circle me-2"></i>
                      Ratings and feedback are typically available after the order
                      is delivered.
                    </div>
                  )}
                {ratingsLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading ratings...</p>
                  </div>
                ) : (
                  <>
                    {order.items.map((item, index) => {
                      const productId = item.product_id;
                      const ratings = productRatings[productId] || [];
                      const hasRatings = ratings.length > 0;

                      return (
                        <div
                          key={index}
                          className={`mb-4 ${index < order.items.length - 1 ? "border-bottom pb-4" : ""}`}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h6 className="fw-bold mb-1">
                                {item.product_name ||
                                  item.product?.name ||
                                  "Unknown Product"}
                              </h6>
                              <small className="text-muted">
                                Product ID: {productId}
                              </small>
                            </div>
                            {hasRatings && (
                              <Badge bg="success">
                                {ratings.length} Rating{ratings.length > 1 ? "s" : ""}
                              </Badge>
                            )}
                          </div>

                          {hasRatings ? (
                            <div className="ratings-list">
                              {ratings.map((rating, ratingIndex) => (
                                <Card
                                  key={ratingIndex}
                                  className="mb-3"
                                  style={{ backgroundColor: "#f8f9fa" }}
                                >
                                  <CardBody>
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center mb-2">
                                          <div className="me-2">
                                            {[...Array(5)].map((_, i) => (
                                              <span
                                                key={i}
                                                style={{
                                                  color:
                                                    i < rating.rating
                                                      ? "#ffc107"
                                                      : "#e0e0e0",
                                                  fontSize: "18px",
                                                }}
                                              >
                                                ★
                                              </span>
                                            ))}
                                          </div>
                                          <span className="fw-bold me-2">
                                            {rating.rating}/5
                                          </span>
                                          {rating.is_verified_purchase && (
                                            <Badge bg="info" className="me-2">
                                              Verified Purchase
                                            </Badge>
                                          )}
                                          {rating.is_approved ? (
                                            <Badge bg="success">Approved</Badge>
                                          ) : (
                                            <Badge bg="warning">Pending</Badge>
                                          )}
                                        </div>
                                        {rating.review_title && (
                                          <h6 className="mb-2 fw-semibold">
                                            {rating.review_title}
                                          </h6>
                                        )}
                                        {rating.review_description && (
                                          <p className="text-muted mb-2">
                                            {rating.review_description}
                                          </p>
                                        )}
                                      </div>
                                      {canDeleteRatings && (
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={() =>
                                            handleDeleteRating(rating.id, productId)
                                          }
                                          className="ms-2"
                                          title="Delete Rating"
                                        >
                                          Delete
                                        </Button>
                                      )}
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <small className="text-muted">
                                          <strong>Customer:</strong>{" "}
                                          {rating.customer_first_name ||
                                            "Unknown"}{" "}
                                          {rating.customer_last_name || ""}
                                        </small>
                                        {rating.customer_email && (
                                          <small className="text-muted d-block">
                                            {rating.customer_email}
                                          </small>
                                        )}
                                      </div>
                                      <div className="text-end">
                                        <small className="text-muted d-block">
                                          {rating.created_at &&
                                            formatDateTimeToDDMMYYYY12Hr(
                                              rating.created_at
                                            )}
                                        </small>
                                        {(rating.is_helpful_count > 0 ||
                                          rating.is_not_helpful_count > 0) && (
                                            <small className="text-muted d-block mt-1">
                                              {rating.is_helpful_count} helpful,{" "}
                                              {rating.is_not_helpful_count} not
                                              helpful
                                            </small>
                                          )}
                                      </div>
                                    </div>
                                  </CardBody>
                                </Card>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-3 bg-light rounded">
                              <i className="fas fa-star fa-2x text-muted mb-2"></i>
                              <p className="text-muted mb-0">
                                No ratings or feedback available for this product
                                in this order
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default OrderDetails;
